import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';

/**
 * Data Mode Context
 * 
 * Global context for managing Demo/Live/Tour data mode across the application.
 * 
 * - Demo Mode: Uses mock/demo data (no backend connectivity)
 * - Live Mode: Uses real backend data (requires backend to be connected)
 * - Tour Mode: Uses tour-specific mock data during product tours
 * 
 * This is distinct from Feature Labs/Beta features which will be implemented separately.
 */

export type DataMode = 'demo' | 'live' | 'tour';

interface DataModeContextValue {
  mode: DataMode;
  setMode: (mode: DataMode) => void;
  isLiveMode: boolean;
  isDemoMode: boolean;
  isTourMode: boolean;
}

const DataModeContext = createContext<DataModeContextValue | undefined>(undefined);

const STORAGE_KEY = 'evo-data-mode';
const DEFAULT_MODE: DataMode = 'demo';

interface DataModeProviderProps {
  children: ReactNode;
}

export function DataModeProvider({ children }: DataModeProviderProps) {
  const [mode, setModeState] = useState<DataMode>(() => {
    // Initialize from localStorage or default to demo
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'demo' || stored === 'live' || stored === 'tour') {
        return stored;
      }
    }
    return DEFAULT_MODE;
  });

  // Persist mode changes to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, mode);
  }, [mode]);

  const setMode = (newMode: DataMode) => {
    localStorage.setItem(STORAGE_KEY, newMode);
    setModeState(newMode);
  };

  const value: DataModeContextValue = {
    mode,
    setMode,
    isLiveMode: mode === 'live',
    isDemoMode: mode === 'demo',
    isTourMode: mode === 'tour',
  };

  return (
    <DataModeContext.Provider value={value}>
      {children}
    </DataModeContext.Provider>
  );
}

export function useDataMode() {
  const context = useContext(DataModeContext);
  if (context === undefined) {
    throw new Error('useDataMode must be used within a DataModeProvider');
  }
  return context;
}
